import { applyProductFilter } from './applyProductFilter';
import {
  optionKeyOf,
  type FilterableProduct,
  type FilterCategory,
  type FilterSelection,
} from './productFilterTypes';

/** Values a group can currently offer, keyed by `categoryId`. */
export type FilterAvailability = Readonly<Record<string, ReadonlySet<string>>>;

/**
 * Which values would still return a product, per group.
 *
 * CMS-1542 R4.4 / R5.4, and Genevieve's point 3 in the sort-and-filter thread
 * (3 Sep): a value that matches nothing is greyed rather than hidden, so the
 * set of options stays stable as the reader ticks boxes.
 *
 * The rule that makes this non-obvious: a group's own selection is **excluded**
 * from the product set its own availability is computed against. Including it
 * would grey out every sibling the moment one value is ticked — a Providers
 * group would collapse to the single provider chosen, which reads exactly like
 * the feature working (AC 1a is that case). That holds for OR groups only: in
 * an AND group a sibling narrows the set, so it is tested together with the
 * group's own selection (FR-3.4.4 — every selectable value returns a result).
 *
 * `undefined` products means "not known" rather than "none", as elsewhere: the
 * caller could not enumerate the page's products, so nothing is greyed. Clara's
 * Q7 answer requires the same during a quote — availability cannot resolve
 * without a result set, so values render ungreyed and resolve on arrival.
 */
export const computeAvailability = (
  categories: readonly FilterCategory[],
  products: readonly FilterableProduct[] | undefined,
  selection: FilterSelection,
  getProduct?: (item: FilterableProduct) => FilterableProduct | undefined,
): FilterAvailability | undefined => {
  if (!products) return undefined;

  const availability: Record<string, ReadonlySet<string>> = {};

  categories.forEach((category) => {
    const categoryId = category.categoryId;
    if (!categoryId) return;

    // Every other group's constraint, this group's own dropped (R5.4).
    const others: FilterSelection = Object.fromEntries(
      Object.entries(selection).filter(([id]) => id !== categoryId),
    );

    const candidates = applyProductFilter(
      products,
      categories,
      others,
      getProduct,
    );

    const own =
      category.combinator === 'AND' ? (selection[categoryId] ?? []) : [];

    const available = new Set<string>();
    category.options.forEach((option) => {
      const matches = applyProductFilter(
        candidates,
        [category],
        { [categoryId]: [...new Set([...own, optionKeyOf(option)])] },
        getProduct,
      );
      if (matches.length > 0) available.add(optionKeyOf(option));
    });

    availability[categoryId] = available;
  });

  return availability;
};
